import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { ApiError, type KnownEntitlement, type OrgDetail, type OrgEntitlement } from "../lib/api";
import { CopyableId } from "./CopyableId";
import { InviteToOrgDialog } from "./InviteToOrgDialog";
import { SetDomainDialog } from "./SetDomainDialog";
import { ChangeSubscriptionDialog } from "./ChangeSubscriptionDialog";

/**
 * Detail panel for one organization: org info + member count, plus the ChargeBee licence
 * (seats used/purchased + entitlements). The "View N members" button deep-links to the Users
 * page filtered to this org. Seats/entitlements come from the subscription grain's cache.
 */
export function OrgDetailPanel({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();

  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [setDomainOpen, setSetDomainOpen] = useState(false);
  const [changeSubOpen, setChangeSubOpen] = useState(false);

  // `clear` blanks the panel while loading — wanted on first load / org switch, not when an
  // entitlement edit refreshes the data underneath the editor.
  const load = useCallback(async (clear = true) => {
    if (clear) setOrg(null);
    setError(null);
    try {
      setOrg(await api.getOrganization(orgId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    }
  }, [api, orgId]);

  useEffect(() => { void load(); }, [load]);

  const sub = org?.subscription;
  const seatsOver =
    sub?.usedSeats != null && sub.purchasedSeats != null && sub.usedSeats > sub.purchasedSeats;

  return (
    <section id="org-detail-panel" className="panel reveal" data-d="1" style={{ marginTop: 22 }}>
      <header>
        <span className="idx">02</span> {org?.name ?? orgId}
        <span className="hint"><span className="mono">{orgId}</span></span>
        {org && (
          <button
            className="btn btn-primary btn-sm"
            style={{ marginLeft: "auto" }}
            onClick={() => setInviteOpen(true)}
            title="Invite users to this organization and optionally set its verified domain"
          >
            Invite users
          </button>
        )}
        {org && (
          <button
            className="btn btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => setSetDomainOpen(true)}
            title="Set or clear this organization's verified domain (blocks that email domain from self-registering)"
          >
            Set Domain
          </button>
        )}
        {org && (
          <button
            className="btn btn-sm"
            style={{ marginLeft: 8 }}
            onClick={() => setChangeSubOpen(true)}
            title="Re-point this organization at a different ChargeBee subscription (must belong to the same ChargeBee customer)"
          >
            Change subscription
          </button>
        )}
        {org && (
          <button
            className="btn btn-sm"
            style={{ marginLeft: 8 }}
            disabled={org.memberCount === 0}
            onClick={() => navigate(`/support/users?org=${encodeURIComponent(org.id)}`)}
            title="Open the Users page filtered to this organization's members"
          >
            View {org.memberCount} member{org.memberCount === 1 ? "" : "s"}
          </button>
        )}
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: org ? 0 : "auto" }} onClick={onClose}>
          Close
        </button>
      </header>
      <div className="body">
        {error && (
          <div className="callout error">
            <span className="mk">err</span>
            <span>{error}</span>
          </div>
        )}
        {!org && !error && <div className="hint">loading…</div>}
        {org && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, alignItems: "start" }}>
              {/* ---------- Organization ---------- */}
              <div>
                <div className="rail-label" style={{ marginBottom: 10 }}>Organization</div>
                <dl className="spec">
                  <dt>name</dt>
                  <dd>{org.name ?? <span className="faint">(unnamed)</span>}</dd>
                  <dt>id</dt>
                  <dd><CopyableId value={org.id} /></dd>
                  <dt>created</dt>
                  <dd className="mono">{fmtDate(org.createdDateTimeUtc)}</dd>
                  <dt>members</dt>
                  <dd className="mono">{org.memberCount}</dd>
                </dl>
              </div>

              {/* ---------- Licence (ChargeBee) ---------- */}
              <div>
                <div className="rail-label" style={{ marginBottom: 10 }}>Licence (ChargeBee)</div>
                {(sub?.chargebeeCustomerUrl || sub?.chargebeeSubscriptionUrl) && (
                  <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 11 }}>
                    {sub.chargebeeCustomerUrl && (
                      <a href={sub.chargebeeCustomerUrl} target="_blank" rel="noreferrer" style={{ color: "var(--cyan)" }}>
                        Customer ↗
                      </a>
                    )}
                    {sub.chargebeeSubscriptionUrl && (
                      <a href={sub.chargebeeSubscriptionUrl} target="_blank" rel="noreferrer" style={{ color: "var(--cyan)" }}>
                        Subscription ↗
                      </a>
                    )}
                  </div>
                )}
                {!sub?.hasSubscription ? (
                  <div className="callout">
                    <span className="mk">!</span>
                    <span style={{ fontSize: 11 }}>
                      No subscription / ChargeBee customer for this organization (test-mode or never
                      subscribed) — no seats or entitlements.
                    </span>
                  </div>
                ) : (
                  <dl className="spec">
                    <dt>seats</dt>
                    <dd>
                      <b className="mono" style={{ color: seatsOver ? "var(--red)" : "var(--lime)" }}>
                        {sub.usedSeats ?? "?"} / {sub.purchasedSeats ?? "?"}
                      </b>{" "}
                      <span className="faint">used / purchased</span>
                    </dd>
                    {sub.planId && (<><dt>plan</dt><dd className="mono">{sub.planId}</dd></>)}
                    {sub.status && (<><dt>status</dt><dd className="mono">{sub.status}</dd></>)}
                    {sub.subscriptionId && (<><dt>subscription</dt><dd className="mono">{sub.subscriptionId}</dd></>)}
                    {sub.currentTermEnd && (<><dt>term ends</dt><dd className="mono">{fmtDate(sub.currentTermEnd)}</dd></>)}
                    <dt>verified domain</dt>
                    <dd className="mono">{sub.verifiedDomain || <span className="faint">—</span>}</dd>
                  </dl>
                )}
              </div>
            </div>

            {/* ---------- Entitlements (full width — the names are long) ---------- */}
            {sub?.hasSubscription && (
              <div style={{ marginTop: 20 }}>
                <div className="rail-label" style={{ marginBottom: 8 }}>Entitlements</div>
                <EntitlementsEditor
                  orgId={org.id}
                  entitlements={sub.entitlements}
                  onChanged={() => load(false)}
                />
              </div>
            )}
          </>
        )}
      </div>
      {inviteOpen && org && (
        <InviteToOrgDialog
          orgId={org.id}
          orgName={org.name ?? orgId}
          onClose={() => setInviteOpen(false)}
          onDone={() => void load(false)}
        />
      )}
      {setDomainOpen && org && (
        <SetDomainDialog
          orgId={org.id}
          orgName={org.name ?? orgId}
          currentDomain={org.subscription?.verifiedDomain ?? null}
          onClose={() => setSetDomainOpen(false)}
          onDone={() => void load(false)}
        />
      )}
      {changeSubOpen && org && (
        <ChangeSubscriptionDialog
          orgId={org.id}
          orgName={org.name ?? orgId}
          currentSubscriptionId={org.subscription?.subscriptionId ?? null}
          onClose={() => setChangeSubOpen(false)}
          onDone={() => void load(false)}
        />
      )}
    </section>
  );
}

/**
 * Entitlement list + administration. Edits write ChargeBee entitlement_overrides via the admin
 * API: upsert adds the feature or overrides its value; remove drops the override so the feature
 * falls back to whatever the plan grants. ChargeBee stays the source of truth — these are the
 * same effective entitlements the app enforces.
 */
function EntitlementsEditor({ orgId, entitlements, onChanged }: {
  orgId: string;
  entitlements: OrgEntitlement[];
  onChanged: () => Promise<void> | void;
}) {
  const api = useApi();
  const [known, setKnown] = useState<KnownEntitlement[]>([]);
  const [editing, setEditing] = useState<string | null>(null); // feature id being value-edited
  const [editValue, setEditValue] = useState("");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // feature id (or ADD) with an in-flight call
  const [error, setError] = useState<string | null>(null);
  const [addFeature, setAddFeature] = useState(""); // known feature id, or CUSTOM
  const [addCustomId, setAddCustomId] = useState("");
  const [addValue, setAddValue] = useState("");

  const ADD = "__add__";
  const CUSTOM = "__custom__";

  useEffect(() => {
    let cancelled = false;
    api.getKnownEntitlements().then(
      (list) => { if (!cancelled) setKnown(list); },
      () => { /* picker degrades to custom-id input */ },
    );
    return () => { cancelled = true; };
  }, [api]);

  async function run(featureId: string, fn: () => Promise<void>) {
    setBusy(featureId);
    setError(null);
    try {
      await fn();
      setEditing(null);
      setConfirmRemove(null);
      if (featureId === ADD) { setAddFeature(""); setAddCustomId(""); setAddValue(""); }
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${e}`);
    } finally {
      setBusy(null);
    }
  }

  const addKind = known.find((k) => k.id === addFeature)?.kind ?? "StringValue";
  const addId = addFeature === CUSTOM ? addCustomId.trim() : addFeature;
  const present = new Set(entitlements.map((e) => e.name));
  const addable = known.filter((k) => !present.has(k.id));

  return (
    <div>
      {entitlements.length === 0 ? (
        <div className="faint" style={{ marginBottom: 8 }}>none</div>
      ) : (
        <dl className="spec spec-kv">
          {entitlements.map((e) => (
            <Fragment key={e.name}>
              <dt className="mono" style={{ fontSize: 10 }} title={e.name}>{e.name}</dt>
              <dd className="mono" title={e.value == null ? undefined : String(e.value)}>
                {editing === e.name ? (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <ValueInput kind={e.kind} value={editValue} onChange={setEditValue} />
                    <button
                      className={"btn btn-sm btn-primary" + (busy === e.name ? " loading" : "")}
                      disabled={busy != null || editValue.trim() === ""}
                      onClick={() => void run(e.name, () => api.setOrgEntitlement(orgId, e.name, editValue.trim()))}
                    >
                      Save
                    </button>
                    <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </span>
                ) : confirmRemove === e.name ? (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <span style={{ color: "var(--red)", fontSize: 10 }}>remove override?</span>
                    <button
                      className={"btn btn-sm btn-danger" + (busy === e.name ? " loading" : "")}
                      disabled={busy != null}
                      onClick={() => void run(e.name, () => api.removeOrgEntitlement(orgId, e.name))}
                    >
                      Confirm
                    </button>
                    <button className="btn btn-sm btn-ghost" disabled={busy != null} onClick={() => setConfirmRemove(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    {fmtEntitlement(e)}
                    <button
                      className="id-copy"
                      title="Edit value (writes a ChargeBee entitlement override)"
                      onClick={() => { setEditing(e.name); setConfirmRemove(null); setEditValue(rawEntitlementValue(e)); }}
                    >
                      ✎
                    </button>
                    <button
                      className="id-copy"
                      title="Remove override — falls back to the plan's value"
                      onClick={() => { setConfirmRemove(e.name); setEditing(null); }}
                    >
                      ✕
                    </button>
                  </span>
                )}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}

      {/* Add a new entitlement (override) */}
      <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={addFeature}
          onChange={(e) => { setAddFeature(e.target.value); setAddValue(""); }}
          style={{ width: "auto", flex: "1 1 180px", fontSize: 11, padding: "7px 28px 7px 10px" }}
        >
          <option value="">add entitlement…</option>
          {addable.map((k) => (
            <option key={k.id} value={k.id}>{k.id}</option>
          ))}
          <option value={CUSTOM}>custom feature id…</option>
        </select>
        {addFeature === CUSTOM && (
          <input
            type="text"
            placeholder="feature-id"
            value={addCustomId}
            onChange={(e) => setAddCustomId(e.target.value)}
            style={{ width: 150, fontSize: 11, padding: "7px 10px" }}
            spellCheck={false}
          />
        )}
        {addFeature !== "" && (
          <>
            <ValueInput kind={addKind} value={addValue} onChange={setAddValue} />
            <button
              className={"btn btn-sm btn-primary" + (busy === ADD ? " loading" : "")}
              disabled={busy != null || addId === "" || addValue.trim() === ""}
              onClick={() => void run(ADD, () => api.setOrgEntitlement(orgId, addId, addValue.trim()))}
            >
              Add
            </button>
          </>
        )}
      </div>
      <div className="hint" style={{ marginTop: 6 }}>
        Edits write ChargeBee entitlement overrides on the subscription; removing one falls back to
        the plan's value.
      </div>

      {error && (
        <div className="callout error" style={{ marginTop: 10 }}>
          <span className="mk">err</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/** Kind-appropriate editor for an entitlement value. Emits the strings ChargeBee expects:
 *  "true"/"false" for switches, a number or "unlimited" for limits, free text otherwise. */
function ValueInput({ kind, value, onChange }: { kind: string; value: string; onChange: (v: string) => void }) {
  if (kind === "BooleanFlag") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: "auto", fontSize: 11, padding: "7px 28px 7px 10px" }}>
        <option value="">value…</option>
        <option value="true">enabled</option>
        <option value="false">disabled</option>
      </select>
    );
  }
  return (
    <input
      type="text"
      placeholder={kind === "LongLimit" ? "number or unlimited" : "value"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: 150, fontSize: 11, padding: "7px 10px" }}
      spellCheck={false}
    />
  );
}

/** The editable string form of an entitlement's current value (what ChargeBee would accept back). */
function rawEntitlementValue(e: OrgEntitlement): string {
  if (e.kind === "BooleanFlag") return e.value ? "true" : "false";
  if (e.kind === "LongLimit") {
    const n = typeof e.value === "number" ? e.value : Number(e.value);
    if (!Number.isFinite(n) || n >= 9e15) return "unlimited";
    return String(n);
  }
  return e.value == null ? "" : String(e.value);
}

function fmtEntitlement(e: OrgEntitlement): string {
  if (e.kind === "BooleanFlag") return e.value ? "enabled" : "disabled";
  if (e.kind === "LongLimit") {
    const n = typeof e.value === "number" ? e.value : Number(e.value);
    // long.MaxValue (the "Unlimited" sentinel) overflows JS safe-int territory.
    if (!Number.isFinite(n) || n >= 9e15) return "unlimited";
    return n.toLocaleString("en-US");
  }
  return e.value == null ? "—" : String(e.value);
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
